import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Modal from "components/base/Modal";
import css from "../style.scss";
import config from 'config';
import ToastNotification from "../../components/ToastNotification";
import refreshBtn from "assets/images/refreshBtn.svg";
import refreshBtnActive from "assets/images/refreshBtnActive.svg";
import Notesimportanticon from "assets/images/Notesimportant.svg";
import ResettableAutocompleteDropdown from "../../../components/SearchDropdown/ResettableAutocompleteDropdown";
import AreaCodeUnavailable from "./AreaCodeUnavailable.js";
import { getStateandAreaIntegration, getCallSettingIntegration } from "../../../data/user/actions";
import { useDispatch, useSelector } from "react-redux";
import { selectStateAreaCodes } from "data/user/selectors";
import { number } from "prop-types";
import { getRandomItems } from "utils/array/getRandomItems";

const SetupPhoneNumber = ({
  isOpen,
  onClose,
  onCancel,
  modalType,
  text,
  setText,
  showPassword,
  togglePasswordVisibility,
  integrationId,
}) => {
  const {
    constants: {
      API: { PRICING_UPDATE_URL },
    },
  } = config;

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [showCopyMsg, setShowCopyMsg] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedAreaCode, setSelectedAreaCode] = useState(null);
  const [apiError, setApiError] = useState("");
  const [toastType, setToastType] = React.useState("success");
  const [resetKey, setResetKey] = useState(0);
  const [selectedNumbers, setSelectedNumbers] = useState(null);

  const [areaCodeDoesntHaveEnoughNumbersError, setAreaCodeDoesntHaveEnoughNumbersError] = useState(false);
  const [showAreaCodeUnavailablePopup, setShowAreaCodeUnavailablePopup] = useState(false);
  const [showPhoneNumbersUpdatedPopup, setShowPhoneNumbersUpdatedPopup] = useState(false);

  const phoneNumbersMaxCount = 3;

  const dispatch = useDispatch();
  const stateAreaCodes = useSelector(selectStateAreaCodes);

  useEffect(() => {
    if (selectedState && selectedAreaCode) {
      getPhonenumber();
    } else if (selectedState) {
      setSelectedAreaCode(null);
    }
  }, [selectedState, selectedAreaCode]);

  const stateItems = stateAreaCodes.map((item) => ({
    id: item.state,
    name: item.state,
  }));

  const handleStateSelect = (state) => {
    setSelectedState(state);
    setSelectedAreaCode(null);
    setSelectedNumbers(null);
  };

  const areaCodeItems = selectedState
    ? (stateAreaCodes
        .find((item) => item.state === selectedState.name)
        ?.areaCodes.map((code) => ({
          id: code,
          name: code,
        })) ?? [])
    : [];

  const handleInternalClose = () => {
    resetForm();
    onCancel && onCancel();
  };

  const formatUSNumber = (number) => {
    if (!number) return "";
    const cleaned = number.startsWith("+1") ? number.slice(2) : number;
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  };

  const removeUSCountryCode = (number) => {
    if (!number) return "";
    return number.startsWith("+1") ? number.slice(2) : number;
  };

  const getPhonenumber = async (afterSetupError = false) => {
    try {
      const params = new URLSearchParams();

      if (selectedState?.name) {
        params.append("state", selectedState.name);
      }

      if (selectedAreaCode?.name) {
        params.append("areaCode", selectedAreaCode.name);
      }

      if (!params.toString()) return;

      const response = await fetch(
        `${PRICING_UPDATE_URL}/ps/integrations/batch-dialer/call-settings/outbound/available?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            "x-auth-token": window.appAuthToken,
          },
        },
      );

      const data = await response.json();

      const numbersCount = data?.numbers.length;

      if (phoneNumbersMaxCount > 0) {
        if (numbersCount >= phoneNumbersMaxCount) {
          const randomlySelectedNumbers = getRandomItems(data.numbers, phoneNumbersMaxCount);

          setSelectedNumbers(randomlySelectedNumbers);
          setAreaCodeDoesntHaveEnoughNumbersError(false);

          setShowPhoneNumbersUpdatedPopup(afterSetupError);
        } else {
          setSelectedNumbers(null);

          if (afterSetupError) {
            setShowAreaCodeUnavailablePopup(true);
            setSelectedState(null);
            setSelectedAreaCode(null);
            setSelectedNumbers(null);
          } else {
            setAreaCodeDoesntHaveEnoughNumbersError(true);
          }
        }
      } else {
        setSelectedNumbers(null);
      }
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    }
  };

  const resetForm = () => {
    onClose();
    setSelectedState(null);
    setSelectedAreaCode(null);
    setSelectedNumbers(null);
    setAreaCodeDoesntHaveEnoughNumbersError(false);
    setShowAreaCodeUnavailablePopup(false);
    setShowPhoneNumbersUpdatedPopup(false);
    setApiError("");
    setIsSubmitting(false);
    setResetKey((prev) => prev + 1);
  };

  const handleSubmit = async () => {
    if (!selectedNumbers) {
      return;
    }

    const payload = {
      batchDialerNumbers: selectedNumbers.map((item) => ({
        trunkId: item.trunkid,
        number: removeUSCountryCode(item.number),
      })),
    };

    try {
      const response = await fetch(`${PRICING_UPDATE_URL}/ps/integrations/batch-dialer/call-settings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-auth-token": window.appAuthToken,
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 200) {
        dispatch(getCallSettingIntegration());
        setToastType("success");
        setToastMessage("You’ve successfully selected your phone numbers.");
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
        resetForm();
      } else {
        const errorData = await response.json().catch(() => ({}));

        if (errorData.message && errorData.message.toLowerCase().includes("some numbers are not available anymore")) {
          getPhonenumber(true);
        }

        setApiError(errorData.message || "Something went wrong");
      }
    } catch (e) {
      setApiError("Network error. Try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showAreaCodeUnavailablePopup) {
    return (
      <AreaCodeUnavailable
        isOpen={true}
        onSuccess={() => {
          setShowAreaCodeUnavailablePopup(false);
        }}
        onClose={() => {
          setShowAreaCodeUnavailablePopup(false);
        }}
      />
    );
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        isCloseButton={false}
        uniqId="CallForwardModal"
        borderRadius="12px"
        zIndex={998}
        modalClassName={css.callsetupModal}
        className={classNames(css.clsAccPageModals, css.clsBillingModal)}
        onClose={() => {
          handleInternalClose();
          onClose && onClose();
        }}
      >
        <div className={css.modalContainer}>
          <div className={css.headertxt}>
            <span>{!showPhoneNumbersUpdatedPopup ? <>Setup Phone Numbers</> : <>Phone Numbers Updated</>}</span>
          </div>

          <div className={classNames(css.instructionText, css.setupPhoneNumberInstructionText)}>
            {!showPhoneNumbersUpdatedPopup ? (
              <>
                <span>Setup your numbers by choosing a state and area code. If your area</span>
                <br />
                <span>code isn’t available, choose a nearby one.</span>
              </>
            ) : (
              <>
                <span>Some of your previously selected numbers are no longer available.</span>
                <br />
                <span>We’ve assigned new numbers for you below</span>
              </>
            )}
          </div>
          <div className={css.inputSection}>
            {!showPhoneNumbersUpdatedPopup && (
              <div className={css.dropdownRow}>
                <div>
                  <ResettableAutocompleteDropdown
                    key={`state-${resetKey}`}
                    placeholder="State"
                    items={stateItems}
                    onSelect={handleStateSelect}
                  />
                </div>
                <div>
                  <ResettableAutocompleteDropdown
                    key={`area-${resetKey}`}
                    placeholder="Area Code"
                    items={areaCodeItems}
                    onSelect={setSelectedAreaCode}
                    disabled={!selectedState}
                  />
                </div>
              </div>
            )}
            {(() => {
              if (areaCodeDoesntHaveEnoughNumbersError) {
                return (
                  <div className={classNames(css.setupPhoneNumberNoStateAreaCodeSelectedContent, css.noteRow)}>
                    <div>
                      This area code is not currently available.
                      <br />
                      Please select a different one.
                    </div>
                  </div>
                );
              }
              if (!selectedState || !selectedAreaCode || !selectedNumbers) {
                return (
                  <div className={classNames(css.setupPhoneNumberNoStateAreaCodeSelectedContent, css.noteRow)}>
                    <div>
                      Select State and Area Code
                      <br />
                      to View Your Numbers
                    </div>
                  </div>
                );
              }
              return (
                <div className={css.setupPhoneNumberContent}>
                  <div className={classNames(css.noteRow, css.setupPhoneNumberContentSection)}>
                    <div className={classNames(css.phonelabel, css.setupPhoneNumberPhoneLabel)}>
                      <span className={css.setupPhoneNumberPhoneLabelText}>Your Numbers</span>
                    </div>
                    <div className={css.setupPhoneNumberContentPhoneNumbers}>
                      {selectedNumbers ? selectedNumbers.map((item) => formatUSNumber(item.number)).join("\n") : null}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
          {!showPhoneNumbersUpdatedPopup && (
            <div className={css.noteRow}>
              <div>
                <img src={Notesimportanticon} alt="Notesimportanticon" />
              </div>
              <div>
                <span className={css.importText}>Numbers can be replaced every 7 days for no additional cost</span>
              </div>
            </div>
          )}
          <div className={css.buttonWrapper}>
            <>
              {!showPhoneNumbersUpdatedPopup && (
                <button className={css.cancelBtn} onClick={handleInternalClose}>
                  <span className={css.cancelBtnLabel}>Cancel</span>
                </button>
              )}
              <button
                className={classNames(css.confirmModalBtn, {
                  [css.disabledBtn]: selectedNumbers == null,
                  [css.noClick]: isSubmitting,
                })}
                disabled={selectedNumbers == null}
                onClick={handleSubmit}
              >
                <span className={css.confirmBtnLabel}>Confirm</span>
              </button>
            </>
          </div>
        </div>
      </Modal>
      {showToast && <ToastNotification message={toastMessage} show={true} type={toastType} />}
    </>
  );
};

export default SetupPhoneNumber;
